import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, RefreshCw, Package, CheckCircle2, Clock, AlertTriangle, Plus, List, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";

const STATUS_STYLE = {
  pending: "bg-slate-100 text-slate-700 border-slate-300",
  in_progress: "bg-sky-100 text-sky-700 border-sky-300",
  delivered: "bg-emerald-100 text-emerald-700 border-emerald-300",
  blocked: "bg-rose-100 text-rose-700 border-rose-300",
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
};
const STATUS_COLS = ["pending", "in_progress", "blocked", "delivered", "cancelled"];

export default function DeliveryBoard() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [spawnFor, setSpawnFor] = useState(null); // {soid, lid, line_name}
  const [view, setView] = useState("list"); // list | kanban
  const [filterClient, setFilterClient] = useState("");
  const [filterBrand, setFilterBrand] = useState("");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/delivery-board`);
      setData(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/projects`).then(({ data }) => setProjects(data || []));
  }, [activeWorkspaceId]);

  const patchLine = async (soid, lid, patch) => {
    try { await api.patch(`/sales-orders/${soid}/delivery-lines/${lid}`, patch); load(); }
    catch { toast.error("Failed"); }
  };

  // Filter helpers — build unique client/brand options + apply filters
  const clientOptions = (() => {
    const seen = new Map();
    (data?.orders || []).forEach((o) => {
      if (o.client_id && !seen.has(o.client_id)) seen.set(o.client_id, o.client_name || o.client_id);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  })();
  const brandOptions = (() => {
    const seen = new Map();
    (data?.orders || []).forEach((o) => {
      if (o.brand_id && !seen.has(o.brand_id)) seen.set(o.brand_id, o.brand_name || o.brand_id);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  })();
  const filteredData = data ? {
    ...data,
    orders: (data.orders || []).filter((o) =>
      (!filterClient || o.client_id === filterClient) &&
      (!filterBrand || o.brand_id === filterBrand)
    ),
  } : data;

  return (
    <div className="p-6 space-y-5" data-testid="delivery-board-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase text-muted-foreground tracking-wide flex items-center gap-1.5">
            <Truck className="w-3 h-3" /> Estimates & Contracts
          </div>
          <h1 className="text-3xl font-bold mt-0.5 tracking-tight">Delivery Board</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every open sales-order line item with delivery date and task links. Financial figures {data?.can_see_money ? "shown" : "hidden"} based on your role.
          </p>
        </div>
        <button type="button" onClick={load} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5" data-testid="delivery-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => setView("list")} className={`text-xs px-3 py-1.5 border flex items-center gap-1 ${view === "list" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid="delivery-view-list">
            <List className="w-3 h-3" /> List
          </button>
          <button type="button" onClick={() => setView("kanban")} className={`text-xs px-3 py-1.5 border flex items-center gap-1 ${view === "kanban" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid="delivery-view-kanban">
            <LayoutGrid className="w-3 h-3" /> Kanban
          </button>
        </div>
        {(clientOptions.length > 0 || brandOptions.length > 0) && (
          <div className="flex items-center gap-1.5 border-l border-border pl-2 ml-1">
            <span className="text-[10px] uppercase text-muted-foreground tracking-wide">Filter:</span>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="text-xs px-2 py-1 bg-background border border-border"
              data-testid="delivery-filter-client"
            >
              <option value="">All clients</option>
              {clientOptions.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="text-xs px-2 py-1 bg-background border border-border"
              data-testid="delivery-filter-brand"
            >
              <option value="">All brands</option>
              {brandOptions.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
            {(filterClient || filterBrand) && (
              <button
                type="button"
                onClick={() => { setFilterClient(""); setFilterBrand(""); }}
                className="text-[10px] px-2 py-1 border border-border hover:bg-muted"
                data-testid="delivery-filter-clear"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {loading && !data && <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>}
      {data && filteredData.orders.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="delivery-empty">
          {data.orders.length === 0 ? "No open sales orders." : "No orders match the current filters."}
        </div>
      )}

      {view === "kanban" && filteredData && (
        <KanbanView data={filteredData} onPatch={patchLine} onSpawn={(soid, lid, name, due) => setSpawnFor({ soid, lid, name, due })} />
      )}

      {view === "list" && (
      <div className="space-y-4">
        {(filteredData?.orders || []).map((o) => (
          <div key={o.id} className="border border-border" data-testid={`delivery-so-${o.id}`}>
            <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4 text-muted-foreground" />
                <span className="font-mono text-xs text-muted-foreground">{o.number || "—"}</span>
                <span className="font-semibold">{o.title || "(untitled)"}</span>
                {o.client_name && <span className="text-xs text-muted-foreground">· {o.client_name}</span>}
                <span className="text-[10px] px-1.5 py-0.5 border bg-muted uppercase">{o.status}</span>
              </div>
              <div className="text-xs text-muted-foreground">{fmtRelative(o.created_at)}</div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-muted/20 border-b border-border">
                <tr className="text-left text-[10px] uppercase text-muted-foreground">
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2 w-16">Qty</th>
                  <th className="px-3 py-2 w-24">Delivery date</th>
                  <th className="px-3 py-2 w-32">Delivery status</th>
                  <th className="px-3 py-2 w-24">Tasks</th>
                  <th className="px-3 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {o.lines.map((li) => (
                  <tr key={li.id} data-testid={`delivery-line-${li.id}`}>
                    <td className="px-3 py-2 font-medium">{li.name || "—"}</td>
                    <td className="px-3 py-2">{li.qty} {li.unit}</td>
                    <td className="px-3 py-2 text-muted-foreground">{li.delivery_date || "—"}</td>
                    <td className="px-3 py-2">
                      <select
                        value={li.delivery_status || "pending"}
                        onChange={(e) => patchLine(o.id, li.id, { delivery_status: e.target.value })}
                        className={`text-[10px] px-1.5 py-0.5 border ${STATUS_STYLE[li.delivery_status || "pending"]}`}
                        data-testid={`delivery-line-status-${li.id}`}
                      >
                        <option value="pending">pending</option>
                        <option value="in_progress">in progress</option>
                        <option value="delivered">delivered</option>
                        <option value="blocked">blocked</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      {li.task_total ? (
                        <span className="text-emerald-700">{li.task_done}/{li.task_total} done</span>
                      ) : (
                        <span className="text-muted-foreground">none</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setSpawnFor({ soid: o.id, lid: li.id, name: li.name, due: li.delivery_date })}
                        className="text-[10px] px-2 py-1 border border-border hover:bg-muted inline-flex items-center gap-1"
                        data-testid={`delivery-line-spawn-${li.id}`}
                      >
                        <Plus className="w-3 h-3" /> Task
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      )}

      {spawnFor && (
        <SpawnTaskDialog
          projects={projects}
          soid={spawnFor.soid} lid={spawnFor.lid}
          defaultTitle={spawnFor.name} defaultDue={spawnFor.due}
          onClose={() => setSpawnFor(null)}
          onDone={() => { setSpawnFor(null); load(); toast.success("Task created"); }}
        />
      )}
    </div>
  );
}

function SpawnTaskDialog({ projects, soid, lid, defaultTitle, defaultDue, onClose, onDone }) {
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState(defaultTitle || "");
  const [assigneeId, setAssigneeId] = useState("");
  const [estHours, setEstHours] = useState("");
  const [due, setDue] = useState(defaultDue || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!projectId) { toast.error("Pick a project"); return; }
    setSaving(true);
    try {
      await api.post(`/sales-orders/${soid}/delivery-lines/${lid}/spawn-task`, {
        project_id: projectId, title: title || undefined,
        assignee_id: assigneeId || undefined,
        est_hours: estHours ? Number(estHours) : undefined,
        due_date: due || undefined,
      });
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="spawn-task-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl">
        <div className="px-4 py-3 border-b border-border bg-muted/40 font-semibold text-sm">Spawn task from delivery line</div>
        <div className="p-4 space-y-3 text-xs">
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="spawn-project">
              <option value="">— Select —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="spawn-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Est. hours</label>
              <input type="number" step="0.5" value={estHours} onChange={(e) => setEstHours(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Due date</label>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" />
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium" data-testid="spawn-save">
            {saving ? "Creating…" : "Create task"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KanbanView({ data, onPatch, onSpawn }) {
  // Flatten all lines from all orders, tagged with SO info
  const allLines = [];
  (data.orders || []).forEach((o) => {
    (o.lines || []).forEach((li) => {
      allLines.push({ ...li, so_id: o.id, so_number: o.number, so_title: o.title, client_name: o.client_name });
    });
  });

  const byStatus = {};
  STATUS_COLS.forEach((s) => (byStatus[s] = []));
  allLines.forEach((li) => {
    const s = li.delivery_status || "pending";
    if (byStatus[s]) byStatus[s].push(li);
    else byStatus.pending.push(li);
  });

  const onDragStart = (e, li) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ soid: li.so_id, lid: li.id, from: li.delivery_status || "pending" }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (e, targetStatus) => {
    e.preventDefault();
    try {
      const raw = e.dataTransfer.getData("text/plain");
      if (!raw) return;
      const { soid, lid, from } = JSON.parse(raw);
      if (from === targetStatus) return;
      onPatch(soid, lid, { delivery_status: targetStatus });
    } catch { /* ignore */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  return (
    <div className="overflow-x-auto" data-testid="delivery-kanban">
      <div className="grid grid-cols-5 gap-3 min-w-[1100px]">
        {STATUS_COLS.map((s) => (
          <div
            key={s}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, s)}
            className={`border ${STATUS_STYLE[s]} min-h-[400px]`}
            data-testid={`kanban-col-${s}`}
          >
            <div className="px-3 py-2 border-b border-border bg-background/50 text-[11px] font-semibold uppercase tracking-wide flex items-center justify-between">
              <span>{s.replace("_", " ")}</span>
              <span className="text-[10px] opacity-70">{byStatus[s].length}</span>
            </div>
            <div className="p-2 space-y-2">
              {byStatus[s].map((li) => (
                <div
                  key={`${li.so_id}-${li.id}`}
                  draggable
                  onDragStart={(e) => onDragStart(e, li)}
                  className="bg-background border border-border p-2 cursor-move hover:shadow-sm"
                  data-testid={`kanban-card-${li.id}`}
                >
                  <div className="text-[10px] font-mono text-muted-foreground">{li.so_number}</div>
                  <div className="text-xs font-medium truncate">{li.name || "—"}</div>
                  <div className="text-[10px] text-muted-foreground mt-1 flex items-center justify-between">
                    <span>{li.qty} {li.unit}</span>
                    {li.delivery_date && <span>{li.delivery_date}</span>}
                  </div>
                  {li.client_name && <div className="text-[10px] text-muted-foreground truncate">{li.client_name}</div>}
                  {li.task_total > 0 && (
                    <div className="text-[10px] text-emerald-700 mt-1">{li.task_done}/{li.task_total} tasks done</div>
                  )}
                  <button
                    type="button"
                    onClick={() => onSpawn(li.so_id, li.id, li.name, li.delivery_date)}
                    className="text-[10px] mt-1.5 text-primary hover:underline"
                    data-testid={`kanban-spawn-${li.id}`}
                  >+ Task</button>
                </div>
              ))}
              {byStatus[s].length === 0 && (
                <div className="text-[10px] text-muted-foreground text-center py-4 opacity-60">Drop here</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
